import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { setGlobal } from 'reactn';
import { Box, Typography, Button, Snackbar } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import MuiAlert from '@material-ui/lab/Alert';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import Datatable from 'material-table';
import AddIcon from '@material-ui/icons/Add';
import SortIcon from '@material-ui/icons/Sort';
import RoleAssignmentDialog from '../roles-assignments-modal';
import DeleteFileDialog from '../../forms/delete-file-dialog';
import {
  getRolesAssignmentList,
  getRolesAssignmentListV2,
  deleteRoleAssignment,
  addRolesAssignment,
  deleteSingleRoleAssignment,
} from '../../../../util/APIUtils';
import { PermissionControl } from '../../../permission-control/permission-control';
import Hoverable from '../../../hoverable';
import RoleTag from '../role-tag';
import { theme } from '../../../../v2/core/containers/theme';
import { UnleashService } from '../../../../v2/services';

/**
 * Legacy material-table roles UX — CREATE_STAFF_V2 disabled.
 * Loads rows from Janus (GET user roles by location); uses global loading overlay.
 */
function RolesAssignmentLegacy({
  classes,
  userDetail,
  isActionAllowed,
  refreshStaffList: refreshStaffListProp,
  onFacilitiesRefresh,
  addButtonLabel,
  addFacilityDialogOpen,
  onAddFacilityDialogClose,
  unleashClient,
}) {
  const [v2StaffRoleLocationEnabled] = useState(() =>
    unleashClient().isEnabled(UnleashService.FLAGS.V2_STAFF_ROLE_LOCATION)
  );

  const [data, setData] = useState(null);
  const [showEmptyDataSourceMessage, setShowEmptyDataSourceMessage] = useState(false);
  const [isNewAssignmentDialogOpen, setIsNewAssignmentDialogOpen] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const refreshStaffList = useCallback(() => {
    refreshStaffListProp?.();
  }, [refreshStaffListProp]);

  const showOpError = useCallback((msg = 'Operation failed, please contact administrator.') => {
    setErrorMsg(msg);
    setShowErrorMsg(true);
  }, []);

  const fetchRolesAssignmentList = useCallback(
    staffName => {
      setGlobal({ isLoading: true });
      setShowEmptyDataSourceMessage(false);
      const req = v2StaffRoleLocationEnabled
        ? getRolesAssignmentListV2(staffName)
        : getRolesAssignmentList(staffName);
      req.then(
        _successLog => {
          setGlobal({ isLoading: false });
          setData(_successLog);
          setShowEmptyDataSourceMessage(true);
        },
        () => {
          setGlobal({ isLoading: false });
          console.error('Something went wrong while fetching...');
        }
      );
    },
    [v2StaffRoleLocationEnabled]
  );

  useEffect(() => {
    const userName = userDetail?.userName;
    if (userName) fetchRolesAssignmentList(userName);
  }, [userDetail?.userName, fetchRolesAssignmentList]);

  const refreshFacilitiesIfNeeded = useCallback(async () => {
    if (!onFacilitiesRefresh) return;
    await onFacilitiesRefresh();
  }, [onFacilitiesRefresh]);

  const onRemoveRole = useCallback(
    async (roleId, locationId) => {
      try {
        await deleteSingleRoleAssignment(roleId, locationId, userDetail.userName);
        fetchRolesAssignmentList(userDetail.userName);
        refreshStaffList();
        await refreshFacilitiesIfNeeded();
      } catch {
        showOpError();
      }
    },
    [userDetail.userName, fetchRolesAssignmentList, refreshStaffList, refreshFacilitiesIfNeeded, showOpError]
  );

  const onAgreeDelete = useCallback(async () => {
    try {
      await deleteRoleAssignment(deleteData.locationId, userDetail.userName);
      fetchRolesAssignmentList(userDetail.userName);
      setIsDeleteDialogOpen(false);
      setDeleteData(null);
      refreshStaffList();
      await refreshFacilitiesIfNeeded();
    } catch {
      showOpError();
    }
  }, [deleteData, userDetail.userName, fetchRolesAssignmentList, refreshStaffList, refreshFacilitiesIfNeeded, showOpError]);

  const submitAssignment = useCallback(
    async values => {
      const payload = {
        locationId: values.location,
        userName: userDetail.userName,
        roleId: values.roles,
      };
      const handleError = error => {
        showOpError(
          error.status === 409 ? 'This location is already assigned to the staff' : 'Operation failed, please contact administrator.'
        );
      };
      setGlobal({ isLoading: true });
      try {
        await addRolesAssignment(payload);
        fetchRolesAssignmentList(userDetail.userName);
        refreshStaffList();
        await refreshFacilitiesIfNeeded();
      } catch (error) {
        handleError(error);
      } finally {
        setGlobal({ isLoading: false });
      }
    },
    [userDetail.userName, fetchRolesAssignmentList, refreshStaffList, refreshFacilitiesIfNeeded, showOpError]
  );

  const handleCloseErrorMsg = useCallback(() => {
    setShowErrorMsg(false);
    setErrorMsg('');
  }, []);

  const isToolbarAllowed = isActionAllowed !== false;

  const tableColumns = useMemo(
    () => [
      {
        title: 'Facility',
        field: 'locationName',
        render: rowData => <Hoverable title={rowData.locationName}>{rowData.locationName}</Hoverable>,
      },
      {
        title: 'Roles',
        field: 'roleNames',
        render: rowData => {
          let roleNames = [];
          let roleIds = [];
          const rolesArray = rowData.roles;
          const useV2RolesShape =
            v2StaffRoleLocationEnabled &&
            Array.isArray(rolesArray) &&
            rolesArray.length > 0;

          if (useV2RolesShape) {
            roleNames = rolesArray.map(r => r.displayName);
            roleIds = rolesArray.map(r => r.roleId);
          } else {
            const namesRaw = rowData.roleNames ?? rowData.roleName ?? '';
            const idsRaw = rowData.roleIds ?? rowData.roleId ?? '';
            roleNames = String(namesRaw)
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
            const idsSplit = String(idsRaw)
              .split(',')
              .map(r => r.trim())
              .filter(Boolean);
            roleIds = roleNames.map((_, i) => idsSplit[i] ?? idsSplit[idsSplit.length - 1] ?? '');
          }
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {roleNames.map((role, index) => {
                const roleId = roleIds[index];
                return (
                  <RoleTag
                    key={roleId || `role-${index}`}
                    onRemoveRole={onRemoveRole}
                    roleId={roleId}
                    locationId={rowData.locationId}
                    isRemovable={roleNames.length > 1}
                  >
                    {role}
                  </RoleTag>
                );
              })}
            </div>
          );
        },
      },
      {
        field: 'delete',
        title: '',
        editable: false,
        filtering: false,
        export: false,
        render: rowData => (
          <PermissionControl
            permissionName="Unassign_Admin_From_Different_Location"
            forceRestriction={(data || []).length < 2}
          >
            <DeleteIcon
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setDeleteData(rowData);
                setIsDeleteDialogOpen(true);
              }}
            />
          </PermissionControl>
        ),
      },
    ],
    [v2StaffRoleLocationEnabled, onRemoveRole, data]
  );

  const dialogOpen =
    addFacilityDialogOpen !== undefined ? addFacilityDialogOpen : isNewAssignmentDialogOpen;

  const handleDialogClose = useCallback(() => {
    if (onAddFacilityDialogClose) onAddFacilityDialogClose();
    setIsNewAssignmentDialogOpen(false);
  }, [onAddFacilityDialogClose]);

  const hideBuiltInToolbar = addButtonLabel && addFacilityDialogOpen !== undefined;

  return (
    <>
      <Snackbar
        open={showErrorMsg}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseErrorMsg}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {errorMsg}
        </MuiAlert>
      </Snackbar>
      <Box width="100%">
        <Datatable
          style={{ width: '100%' }}
          columns={tableColumns}
          data={data || []}
          components={{
            Toolbar: () =>
              hideBuiltInToolbar ? null : (
                <div>
                  <Box className={classes.titleBox}>
                    <Typography component="span" className={classes.tabName}>
                      Roles
                    </Typography>
                    {isToolbarAllowed && (
                      <PermissionControl permissionName="Assign_Admin_To_Different_Location">
                        <Button
                          className={classes.addBtn}
                          color="primary"
                          variant="contained"
                          onClick={() => setIsNewAssignmentDialogOpen(true)}
                        >
                          <AddIcon />
                        </Button>
                      </PermissionControl>
                    )}
                  </Box>
                </div>
              ),
          }}
          detailPanel={[
            {
              tooltip: 'Roles Details',
              icon: ExpandMoreIcon,
              render: rowData => (
                <div
                  style={{
                    fontSize: 100,
                    textAlign: 'center',
                    width: '100%',
                    height: '315',
                    color: theme.palette.text.red,
                    backgroundColor: 'red',
                  }}
                >
                  {rowData.name}
                </div>
              ),
            },
          ]}
          options={{
            sorting: false,
            pageSize: 20,
            selection: false,
            showSelectAllCheckbox: true,
            filtering: false,
            Sort: SortIcon,
            showEmptyDataSourceMessage,
            SortArrow: ArrowDownwardIcon,
          }}
          icons={{
            FirstPage: FirstPageIcon,
            LastPage: LastPageIcon,
            NextPage: NavigateNextIcon,
            PreviousPage: NavigateBeforeIcon,
          }}
        />

        <RoleAssignmentDialog
          open={dialogOpen}
          handleClose={handleDialogClose}
          userDetail={userDetail}
          submitAssignment={submitAssignment}
          useAddFacilityOnly={false}
          createStaffV2Enabled={false}
        />

        <DeleteFileDialog
          onAgree={onAgreeDelete}
          isOpen={!!isDeleteDialogOpen}
          handleClose={() => {
            setDeleteData(null);
            setIsDeleteDialogOpen(false);
          }}
        />
      </Box>
    </>
  );
}

RolesAssignmentLegacy.defaultProps = {
  onFacilitiesRefresh: null,
};

export default RolesAssignmentLegacy;
